import MobileDetect from 'mobile-detect';

import {
  ADSTATE_CONTAINERS,
  UI,
  APP,
  PRESENT,
  PREVIEW,
  LAYOUTS,
} from 'SharedConstants/';

import {
  MOBILE_PORTRAIT_DEVICE_DIMENSIONS,
  MOBILE_LANDSCAPE_DEVICE_DIMENSIONS,
  TABLET_PORTRAIT_DEVICE_DIMENSIONS,
  TABLET_LANDSCAPE_DEVICE_DIMENSIONS,
  SELECTED_CONTAINER,
  SELECTED_LAYOUT,
} from 'PreviewConstants/';

const getDimensionsObject = dimensions => ({ width: dimensions[0], height: dimensions[1] });

export const getDeviceDimensions = (device) => {
  if (device === 'mobile-portrait') return getDimensionsObject(MOBILE_PORTRAIT_DEVICE_DIMENSIONS);
  if (device === 'mobile-landscape') return getDimensionsObject(MOBILE_LANDSCAPE_DEVICE_DIMENSIONS);
  if (device === 'tablet-portrait') return getDimensionsObject(TABLET_PORTRAIT_DEVICE_DIMENSIONS);
  if (device === 'tablet-landscape') return getDimensionsObject(TABLET_LANDSCAPE_DEVICE_DIMENSIONS);
  return getDimensionsObject(MOBILE_PORTRAIT_DEVICE_DIMENSIONS);
};

export const getDeviceScale = (device) => {
  const deviceDimensions = getDeviceDimensions(device);
  return window.innerWidth / deviceDimensions.width;
};

export const getLayoutContainerRect = () => {
  const siteBackgroundContainer = document.querySelector('.site-background');
  return siteBackgroundContainer ? siteBackgroundContainer.getBoundingClientRect() : null;
};

export const getPreviewElementGeometry = (geom) => {
  const backgroundCSS = {};
  const left = geom.get(0);
  const top = 'auto'; // geom.get(1);
  const width = geom.get(2);
  const height = geom.get(3);

  backgroundCSS.left = '0';
  backgroundCSS.right = 'auto';
  backgroundCSS.marginLeft = '0';

  if (left === 'centre') {
    backgroundCSS.left = '50%';
    backgroundCSS.marginLeft = `-${width / 2}px`;
  } else if (left === 'right') {
    backgroundCSS.right = '0';
  }

  backgroundCSS.top = top;
  backgroundCSS.width = width;
  backgroundCSS.height = height;
  return backgroundCSS;
};

export const getContainerGeometry = (adStateContainer, deviceScale = 1) => {
  const geom = adStateContainer.dimensions;
  const backgroundCSS = {};
  const left = geom.get(0);
  const top = geom.get(1);
  const width = geom.get(2);
  const height = geom.get(3);

  backgroundCSS.left = left * deviceScale;
  backgroundCSS.right = 'auto';
  backgroundCSS.marginLeft = '0';
  backgroundCSS.position = adStateContainer.isFixed ? 'fixed' : 'absolute';
  backgroundCSS.transform = 'none';

  if (left === 'centred') {
    backgroundCSS.left = '50%';
    backgroundCSS.marginLeft = `-${width / 2}px`;
  } else if (left === 'site-left') {
    backgroundCSS.position = 'fixed';
  } else if (left === 'site-right') {
    backgroundCSS.left = 'auto';
    backgroundCSS.position = 'fixed';
  } else if (left === 'right') {
    backgroundCSS.right = '0';
  }

  backgroundCSS.top = top * deviceScale;
  backgroundCSS.width = width * deviceScale;
  backgroundCSS.height = height * deviceScale;
  return backgroundCSS;
};

export const getLayoutBackgroundCSS = (device = 'desktop', isDevice = false, align, size) => {
  const backgroundCSS = {};
  const width = size.get(0);
  const height = size.get(1);

  backgroundCSS.left = '0';
  backgroundCSS.top = top;
  backgroundCSS.width = width;
  backgroundCSS.height = height;

  if (device !== 'desktop') { // && !isDevice
    let deviceWidth;
    if (isDevice) {
      deviceWidth = window.innerWidth;
    } else {
      switch (device) {
        case 'mobile-landscape':
          deviceWidth = MOBILE_LANDSCAPE_DEVICE_DIMENSIONS[0];
          break;
        case 'tablet-portrait':
          deviceWidth = TABLET_PORTRAIT_DEVICE_DIMENSIONS[0];
          break;
        case 'tablet-landscape':
          deviceWidth = TABLET_LANDSCAPE_DEVICE_DIMENSIONS[0];
          break;
        default:
          deviceWidth = MOBILE_PORTRAIT_DEVICE_DIMENSIONS[0];
          break;
      }
    }

    const deviceScale = deviceWidth / width;
    backgroundCSS.width = '100%';
    backgroundCSS.height = `${(height * deviceScale)}px`;
  } else if (align === 'centre') {
    backgroundCSS.left = '50%';
    backgroundCSS.marginLeft = `-${width / 2}px`;
  } else if (align === 'right') {
    backgroundCSS.right = '0';
  }

  return backgroundCSS;
};

export const getURLSafeLayoutName = name => (name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().match(/[^-]+/g).join('-'));

const getLayout = (state, layouts) => {
  const selectedLayout = state.getIn([UI, SELECTED_LAYOUT]);
  if (selectedLayout && isNaN(selectedLayout)) {
    return layouts.find(layout => getURLSafeLayoutName(layout.name) === selectedLayout);
  }
  return layouts.find(layout => layout.key === selectedLayout);
};

export const getAvailableLayouts = (state) => {
  const availableLayouts = state.getIn([APP, PRESENT, PREVIEW, LAYOUTS]);
  return availableLayouts.filter(layout => layout.isCustom !== true);
};

export const getSelectedLayout = state => (getLayout(state, getAvailableLayouts(state)));

export const getAvailableCustomLayouts = (state) => {
  const availableLayouts = state.getIn([APP, PRESENT, PREVIEW, LAYOUTS]);
  return availableLayouts.filter(layout => layout.isCustom === true);
};

export const getSelectedCustomLayout = state => (getLayout(state, getAvailableCustomLayouts(state)));

export const getActivePageLayout = (state) => {
  const selectedContainer = state.getIn([UI, SELECTED_CONTAINER]);
  const getLayoutFunction = (selectedContainer === 'custom') ? getSelectedCustomLayout : getSelectedLayout;
  return getLayoutFunction(state);
};

export const getPreview = state => state.getIn([APP, PRESENT, PREVIEW]);

export const getIsDevice = () => {
  const { navigator: { userAgent } } = global.window;
  const mobileDetect = new MobileDetect(userAgent);
  return mobileDetect.phone();
};

export const getHexFromImage = url => (new Promise((resolve, reject) => {
  const canvas = document.createElement('canvas');
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = `/assets/proxy?a=${url}`;
  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const c = canvas.getContext('2d');
    const p = c.getImageData(1, 1, 1, 1).data;
    const rgb = `rgb(${p[0]},${p[1]},${p[2]})`;
    resolve(rgb);
  };
  img.onerror = reject;
}));

export const setQueryParams = (selectedContainer, activeLayout) => {
  const safeName = activeLayout ? getURLSafeLayoutName(activeLayout.name) : '';
  const previewPath = (selectedContainer === 'responsive') ? selectedContainer : `${selectedContainer}/${safeName}`;
  history.replaceState(null, null, `?${previewPath}`);
};

export const setPageBackgroundColour = (imageUrl) => {
  if (imageUrl.includes('missing.png')) return;
  getHexFromImage(imageUrl).then((color) => {
    const siteBackground = document.querySelector('.site-background');
    siteBackground.style.backgroundColor = color;
  }).catch(error => console.log(error));
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/selectors/index.js